//frontend/components/admin/AdminPproducts.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Edit,
    Trash2,
    Plus,
    Tag,
    Search,
    RefreshCw,
    Package,
    ArrowLeft,
    Eye,
    EyeOff
} from 'lucide-react';
import axios from '../../utils/axiosConfig';
import { Card } from '../../components/ui/card';

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(false);
    const navigate = useNavigate();

    const fetchProducts = async (forceRefresh = false) => {
        try {
            setLoading(true);
            let url = '/admin/products';
            if (forceRefresh) {
                url += `?_force=${Date.now()}&_refresh=${Math.random().toString(36).substring(7)}`;
            }
            
            const { data } = await axios.get(url, {
                withCredentials: true
            });
            
            if (data.success) {
                setProducts(data.products);
                if (forceRefresh) {
                    toast.success('Products refreshed successfully');
                }
            }
        } catch (err) {
            console.error('Error details:', err);
            toast.error(err.response?.data?.message || 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleDeleteProduct = async (productId) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                setActionLoading(true);
                const { data } = await axios.delete(`/admin/product/${productId}`, {
                    withCredentials: true
                });
                if (data.success) {
                    toast.success('Product deleted successfully');
                    fetchProducts();
                }
            } catch (err) {
                toast.error(err.response?.data?.message || 'Error deleting product');
            } finally {
                setActionLoading(false);
            }
        }
    };

    // Function to toggle product visibility
    const toggleProductVisibility = async (productId, currentVisibility) => {
        try {
            setActionLoading(true);
            const { data } = await axios.patch(
                `/admin/product/${productId}/toggle-visibility`,
                {}, // Empty body, the toggle is handled on the server
                { withCredentials: true }
            );
            
            if (data.success) {
                // Update local state to reflect the change
                setProducts(products.map(product =>
                    product._id === productId
                        ? { ...product, isVisible: data.isVisible }
                        : product
                ));
                
                toast.success(`Product is now ${data.isVisible ? 'visible' : 'hidden'}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error toggling product visibility');
        } finally {
            setActionLoading(false);
        }
    };

    // Function to toggle weight option visibility
    const handleToggleWeightOption = async (productId, weight) => {
        try {
            setActionLoading(true);
            const { data } = await axios.patch(
                `/admin/product/${productId}/weight/${weight}/toggle-visibility`,
                {},
                { withCredentials: true }
            );
            
            if (data.success) {
                // Update the local state to reflect the change
                setProducts(products.map(product => {
                    if (product._id === productId) {
                        const updatedWeightOptions = product.weightOptions.map(option => {
                            if (option.weight === weight) {
                                return { ...option, visible: !option.visible };
                            }
                            return option;
                        });
                        return { ...product, weightOptions: updatedWeightOptions };
                    }
                    return product;
                }));
                
                toast.success(`${weight} option visibility toggled successfully`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error toggling weight option visibility');
        } finally {
            setActionLoading(false);
        }
    };

    const getPrice = (product) => {
        if (product.priceType === 'perKg') {
            return `₹${product.price}/kg`;
        } else if (product.priceType === 'perDozen') {
            return `₹${product.dozenPrice}/dozen`;
        } else if (product.priceType === 'perPack') {
            return `₹${product.packPrice}/pack`;
        } else if (product.weightOptions && product.weightOptions.length > 0) {
            // Filter for visible weight options only
            const visibleOptions = product.weightOptions.filter(opt => opt.visible);
            if (visibleOptions.length === 0) {
                return 'No visible options';
            }
            const prices = visibleOptions.map(opt => opt.price);
            return `₹${Math.min(...prices)} - ₹${Math.max(...prices)}`;
        }
        return 'Price not available';
    };

    const getStockStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'instock':
                return 'bg-green-100 text-green-800';
            case 'lowstock':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-red-100 text-red-800';
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    // Mobile product card component with visibility toggle
    const ProductCard = ({ product }) => (
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3 border border-gray-100">
            <div className="flex items-start gap-3">
                <img
                    src={product.images[0].image}
                    alt={product.name}
                    className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1">
                    <div className="flex justify-between">
                        <h3 className="text-base font-medium text-gray-900">{product.name}</h3>
                        <span className={`text-xs ${product.isVisible ? 'text-green-600' : 'text-gray-500'}`}>
                            {product.isVisible ? 'Visible' : 'Hidden'}
                        </span>
                    </div>
                    <div className="flex items-center mt-0.5">
                        <Tag className="h-3 w-3 text-gray-500 mr-1" />
                        <span className="text-xs text-gray-600">{product.category}</span>
                    </div>
                    <div className="text-[#dc2626] font-semibold mt-0.5 text-sm">
                        {getPrice(product)}
                    </div>
                    
                    {/* Weight Options Visibility Toggle (Mobile) */}
                    {product.priceType === 'fixed' && product.weightOptions && (
                        <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Weight Options:</p>
                            <div className="flex flex-wrap gap-1">
                                {product.weightOptions.map(option => (
                                    <button
                                        key={option.weight}
                                        onClick={() => handleToggleWeightOption(product._id, option.weight)}
                                        className={`px-2 py-0.5 text-xs rounded ${option.visible 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-gray-100 text-gray-800'}`}
                                        disabled={actionLoading}
                                    >
                                        {option.weight} {option.visible ? '✓' : '×'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-2">
                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${getStockStatusColor(product.stockStatus)}`}>
                            {product.stockStatus.toUpperCase()}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => toggleProductVisibility(product._id, product.isVisible)}
                                className={`p-1.5 ${product.isVisible ? 'text-green-600 hover:bg-green-50' : 'text-gray-500 hover:bg-gray-50'} rounded-full transition-colors`}
                                disabled={actionLoading}
                            >
                                {product.isVisible ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button
                                onClick={() => navigate(`/admin/product/${product._id}/edit`)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                disabled={actionLoading}
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                onClick={() => handleDeleteProduct(product._id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                disabled={actionLoading}
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <RefreshCw className="w-6 h-6 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    const categories = ['all', ...new Set(products.map(p => p.category))];

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button - Now aligned to the right */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                </button>
            </div>
            <Card className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <h2 className="text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                            Products Management
                        </h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => fetchProducts(true)}
                                className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
                                title="Refresh Products"
                            >
                                <RefreshCw className="w-4 h-4 mr-1.5" />
                                Refresh
                            </button>
                            <button
                                onClick={() => navigate('/admin/product/new')}
                                className="bg-[#dc2626] text-white px-3 py-1.5 rounded-lg hover:bg-[#dc2626]/90 transition-colors flex items-center justify-center text-sm"
                            >
                                <Plus className="w-4 h-4 mr-1.5" />
                                Add New Product
                            </button>
                        </div>
                    </div>
                    {/* Filters Section */}
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#dc2626] focus:border-transparent text-xs"
                            />
                            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                        </div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#dc2626] focus:border-transparent text-xs"
                        >
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Desktop View with Visibility Toggle */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price Range</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Visibility</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredProducts.map((product) => (
                                <tr key={product._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center">
                                            <img
                                                className="h-10 w-10 rounded-lg object-cover"
                                                src={product.images[0].image}
                                                alt={product.name}
                                            />
                                            <div className="ml-3">
                                                <div className="font-medium text-xs text-gray-900">{product.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center">
                                            <Tag className="h-3 w-3 text-gray-500 mr-1.5" />
                                            <span className="text-xs text-gray-900">{product.category}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-[#dc2626] font-medium text-xs">{getPrice(product)}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${getStockStatusColor(product.stockStatus)}`}>
                                            {product.stockStatus.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                                            product.isVisible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {product.isVisible ? 'VISIBLE' : 'HIDDEN'}
                                        </span>
                                        
                                        {/* Weight Options Visibility (Desktop) */}
                                        {product.priceType === 'fixed' && product.weightOptions && (
                                            <div className="mt-1.5">
                                                <div className="flex flex-wrap gap-1">
                                                    {product.weightOptions.map(option => (
                                                        <button
                                                            key={option.weight}
                                                            onClick={() => handleToggleWeightOption(product._id, option.weight)}
                                                            className={`px-1.5 py-0.5 text-xxs rounded ${option.visible 
                                                                ? 'bg-green-50 text-green-700 border border-green-200' 
                                                                : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
                                                            title={option.visible ? `Hide ${option.weight}` : `Show ${option.weight}`}
                                                            disabled={actionLoading}
                                                        >
                                                            {option.weight} {option.visible ? '✓' : '×'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1.5">
                                            <button
                                                onClick={() => toggleProductVisibility(product._id, product.isVisible)}
                                                className={`p-1.5 ${product.isVisible ? 'text-green-600 hover:bg-green-50' : 'text-gray-500 hover:bg-gray-50'} rounded-full transition-colors`}
                                                title={product.isVisible ? 'Hide Product' : 'Show Product'}
                                                disabled={actionLoading}
                                            >
                                                {product.isVisible ? <Eye size={16} /> : <EyeOff size={16} />}
                                            </button>
                                            <button
                                                onClick={() => navigate(`/admin/product/${product._id}/edit`)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                                title="Edit Product"
                                                disabled={actionLoading}
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteProduct(product._id)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                                title="Delete Product"
                                                disabled={actionLoading}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="md:hidden p-3">
                    {filteredProducts.map((product) => (
                        <ProductCard key={product._id} product={product} />
                    ))}
                </div>

                {/* Empty State */}
                {filteredProducts.length === 0 && (
                    <div className="text-center py-8">
                        <Package className="mx-auto h-10 w-10 text-gray-400" />
                        <h3 className="mt-2 text-xs font-medium text-gray-900">No Products Found</h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {searchTerm || categoryFilter !== 'all' ?
                                'No products match your search criteria.' :
                                'Start by adding some products.'}
                        </p>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default AdminProducts;